import React from "react";
import { Pressable, StyleSheet, View } from "react-native";
import { ArrowLeftIcon, ArrowRightIcon, CheckCircleIcon, ClockCounterClockwiseIcon } from "@phosphor-icons/react";

import CustomButton from "../../../../../components/common/general/CustomButton/CustomButton";
import CustomProgressBar from "../../../../../components/common/general/CustomProgressBar/CustomProgressBar";
import CustomText from "../../../../../components/common/general/CustomText/CustomText";
import type { CourseEvaluatorReviewData } from "../types/reviewTypes";
import { colors } from "../../../../../styles/design-system";

interface ReviewTopBarProps {
  review: CourseEvaluatorReviewData;
  reviewedProgress: number;
  lastSavedAt?: string | null;
  isSaving?: boolean;
  publishHistoryCount?: number;
  pendingManualStepsCount?: number;
  onExitReview: () => void;
  onOpenPublishSheet?: () => void;
  onOpenPublishHistory?: () => void;
}

function ReviewTopBar({
  review,
  reviewedProgress,
  lastSavedAt,
  isSaving,
  publishHistoryCount = 0,
  pendingManualStepsCount = 0,
  onExitReview,
  onOpenPublishSheet,
  onOpenPublishHistory,
}: ReviewTopBarProps) {
  const hasQueued = review.queuedCount > 0;
  const canOpenReadySheet = hasQueued;
  const hasPublishHistory = publishHistoryCount > 0;
  const progressColor = reviewedProgress === 100 ? colors.success[500] : colors.brand[500];
  const readyActionText = review.canPublishToLms
    ? `Ready to Publish (${review.queuedCount})`
    : `Ready to Download (${review.queuedCount})`;

  return (
    <View style={styles.topBar}>
      <View style={styles.topBarLeft}>
        <CustomButton
          text="Exit Review"
          styleType="transparent"
          size="s"
          useTranslationText={false}
          onPress={onExitReview}
          style={styles.exitButton}
          textStyle={styles.exitButtonText}
          leftIcon={<ArrowLeftIcon size={18} color={colors.grey[700]} />}
        />

        <View style={styles.topBarDivider} />

        <View style={styles.titleRow}>
          <CustomText
            text={`${review.courseName} (${review.sourceLabel})`}
            useTranslationText={false}
            size="m"
            weight="bold"
            role="heading"
            aria-level="1"
            style={styles.topBarTitle}
          />
          <CustomText text={review.courseCode} useTranslationText={false} size="s" style={styles.topBarMeta} />
          <CustomText
            text={review.termLabel}
            useTranslationText={false}
            size="xs"
            weight="normal"
            style={styles.topBarMeta}
          />
        </View>
      </View>

      <View style={styles.topBarRight}>
        {/* Reviewed count */}
        <CustomText
          text={`${review.reviewedCount}/${review.totalCount}`}
          useTranslationText={false}
          size="xs"
          weight="medium"
          style={styles.reviewedCount}
        />
        <CustomText text="reviewed" useTranslationText={false} size="xs" weight="normal" style={styles.reviewedLabel} />

        {/* Progress bar */}
        <View style={styles.topProgressWrap}>
          <CustomProgressBar
            ariaLabel="Review progress"
            progress={reviewedProgress}
            color={progressColor}
            unfilledColor={colors.grey[100]}
          />
        </View>

        {/* Saved indicator — passive text, not a button */}
        {(lastSavedAt || isSaving) && (
          <View style={styles.savedRow}>
            <CheckCircleIcon size={12} color={colors.success[500]} weight="fill" />
            <CustomText
              text={isSaving ? "Saving..." : `Saved ${lastSavedAt}`}
              useTranslationText={false}
              size="xs"
              style={styles.savedText}
            />
          </View>
        )}

        {hasPublishHistory && (
          <Pressable
            onPress={onOpenPublishHistory}
            style={styles.historyButton}
            accessibilityLabel="Open publish history">
            <ClockCounterClockwiseIcon size={14} color={colors.grey[700]} weight="regular" />
            <CustomText
              text="History"
              useTranslationText={false}
              size="xs"
              weight="medium"
              style={styles.historyButtonText}
            />
            {pendingManualStepsCount > 0 && (
              <View style={styles.historyBadge}>
                <CustomText
                  text={`${pendingManualStepsCount}`}
                  useTranslationText={false}
                  size="xs"
                  weight="bold"
                  style={styles.historyBadgeText}
                />
              </View>
            )}
          </Pressable>
        )}

        {/* Ready action — publish-capable LMS courses publish, uploaded courses download exports. */}
        {canOpenReadySheet ? (
          <CustomButton
            text={readyActionText}
            useTranslationText={false}
            onPress={onOpenPublishSheet ?? (() => {})}
            styleType="primary"
            size="s"
            rightIcon={<ArrowRightIcon size={16} color={colors.white} />}
            style={styles.publishButtonActive}
            textStyle={styles.publishButtonActiveText}
          />
        ) : (
          <CustomButton
            text={readyActionText}
            useTranslationText={false}
            onPress={() => {}}
            disabled
            styleType="lightStroke"
            size="s"
            rightIcon={<ArrowRightIcon size={16} color={colors.grey[400]} />}
            style={styles.publishButton}
            textStyle={styles.publishButtonText}
          />
        )}
      </View>
    </View>
  );
}

const styles = StyleSheet.create({
  topBar: {
    height: 56,
    borderBottomWidth: 1,
    borderBottomColor: colors.grey[200],
    backgroundColor: "#FFFFFF",
    flexDirection: "row",
    alignItems: "center",
    justifyContent: "space-between",
    paddingHorizontal: 16,
  },
  topBarLeft: {
    flexDirection: "row",
    alignItems: "center",
    flex: 1,
    minWidth: 0,
  },
  exitButton: {
    paddingHorizontal: 8,
    paddingVertical: 8,
  },
  exitButtonText: {
    color: colors.grey[700],
    fontSize: 12,
    fontWeight: "500",
  },
  topBarDivider: {
    width: 1,
    height: 24,
    backgroundColor: colors.grey[200],
    marginHorizontal: 16,
  },
  titleRow: {
    flexDirection: "row",
    alignItems: "center",
    gap: 16,
    flexWrap: "wrap",
    flex: 1,
  },
  topBarTitle: {
    color: colors.grey[900],
  },
  topBarMeta: {
    color: colors.grey[500],
  },
  topBarRight: {
    flexDirection: "row",
    alignItems: "center",
    gap: 8,
    marginLeft: 24,
  },
  reviewedCount: {
    color: colors.grey[900],
  },
  reviewedLabel: {
    color: colors.grey[500],
  },
  topProgressWrap: {
    width: 80,
  },
  savedRow: {
    flexDirection: "row",
    alignItems: "center",
    gap: 4,
  },
  savedText: {
    color: colors.grey[500],
    fontSize: 11,
  },
  historyButton: {
    height: 28,
    flexDirection: "row",
    alignItems: "center",
    gap: 6,
    paddingHorizontal: 10,
    borderRadius: 6,
    borderWidth: 1,
    borderColor: colors.grey[200],
    backgroundColor: colors.white,
  },
  historyButtonText: {
    color: colors.grey[700],
    fontSize: 12,
  },
  historyBadge: {
    minWidth: 16,
    height: 16,
    borderRadius: 999,
    alignItems: "center",
    justifyContent: "center",
    paddingHorizontal: 4,
    backgroundColor: colors.warning[100],
  },
  historyBadgeText: {
    color: colors.warning[700],
    fontSize: 10,
    lineHeight: 12,
  },
  publishButton: {
    borderRadius: 6,
    paddingHorizontal: 10,
    height: 28,
    borderColor: colors.grey[200],
    shadowColor: "transparent",
    elevation: 0,
  },
  publishButtonText: {
    color: colors.grey[400],
    fontSize: 12,
    fontWeight: "500",
  },
  publishButtonActive: {
    borderRadius: 6,
    paddingHorizontal: 10,
    height: 28,
    backgroundColor: colors.brand[600],
    borderColor: colors.brand[600],
    shadowColor: "transparent",
    elevation: 0,
  },
  publishButtonActiveText: {
    color: colors.white,
    fontSize: 12,
    fontWeight: "500",
  },
});

export default ReviewTopBar;
